/**
 * Active power-up effect chips — pure logic, rendered by Hud.
 *
 * Timed effects (overdrive / 5-way) show a draining bar + seconds countdown
 * against SERVER time (the same clock the server stamped the expiry with, so
 * client clock skew cannot lie about remaining time). The armour chip shows
 * the remaining over-armour pool instead of a countdown.
 */
import {
  ARMOUR_MAX,
  OVERDRIVE_DURATION_MS,
  SPREAD5_DURATION_MS,
} from "@neon-arena/shared";

export interface EffectChip {
  /** Stable id — also used as the CSS class suffix. */
  id: "overdrive" | "spread5" | "armour";
  label: string;
  /** 0..1 fill fraction for the chip's bar. */
  frac: number;
  /** Short trailing text (e.g. "3.2s" or "50"). */
  detail: string;
  /** Chip accent colour (matches the in-world pickup colour). */
  color: string;
}

export interface EffectSource {
  overdriveUntil?: number;
  spread5Until?: number;
  armour?: number;
}

export function buildEffectChips(me: EffectSource, serverTime: number): EffectChip[] {
  const chips: EffectChip[] = [];
  if (serverTime > 0) {
    const od = (me.overdriveUntil ?? 0) - serverTime;
    if (od > 0) {
      chips.push({
        id: "overdrive",
        label: "OVERDRIVE",
        frac: Math.min(1, od / OVERDRIVE_DURATION_MS),
        detail: `${(od / 1000).toFixed(1)}s`,
        color: "#fb923c",
      });
    }
    const sp = (me.spread5Until ?? 0) - serverTime;
    if (sp > 0) {
      chips.push({
        id: "spread5",
        label: "5-WAY",
        frac: Math.min(1, sp / SPREAD5_DURATION_MS),
        detail: `${(sp / 1000).toFixed(1)}s`,
        color: "#facc15",
      });
    }
  }
  const armour = me.armour ?? 0;
  if (armour > 0) {
    chips.push({
      id: "armour",
      label: "ARMOUR",
      frac: Math.min(1, armour / ARMOUR_MAX),
      detail: `${Math.round(armour)}`,
      color: "#4ade80",
    });
  }
  return chips;
}

/** Render chips to an HTML string (Hud sets innerHTML with this). */
export function renderEffectChipsHtml(chips: EffectChip[]): string {
  return chips
    .map(
      (c) =>
        `<div class="effect-chip effect-chip-${c.id}" style="border-color:${c.color};color:${c.color}">` +
        `<span class="effect-chip-label">${c.label}</span>` +
        `<span class="effect-chip-track"><span class="effect-chip-fill" style="width:${Math.round(c.frac * 100)}%;background:${c.color}"></span></span>` +
        `<span class="effect-chip-detail">${c.detail}</span>` +
        `</div>`,
    )
    .join("");
}
